<div class="composer-shell">
    <input #imageAttachmentInput
        class="composer-hidden-input"
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        (change)="onImageFileInputChange($event)" />

    <input #fileAttachmentInput
        class="composer-hidden-input"
        type="file"
        multiple
        (change)="onFileInputChange($event)" />

    <div class="composer-suggestions-section composer-suggestions-section--above" *ngIf="shouldShowSuggestionsAbove">
        <div class="composer-suggestions-label">Suggested prompts</div>
        <div class="composer-suggestions">
            <button class="composer-suggestion"
                    *ngFor="let playbook of contextPrompts"
                    type="button"
                    (click)="onContextPromptClick(playbook)"
                    [attr.aria-label]="'Use playbook ' + playbook.title">
                <i class="fa-regular {{ playbook.icon }} composer-suggestion-icon"></i>
                <span>{{ playbook.title }}</span>
            </button>
        </div>
    </div>

<div class="composer" #composerEl (click)="$event.stopPropagation()">
    <!-- Inline playbook template (replaces textarea when active) -->
    <div class="playbook-inline" *ngIf="activePlaybook; else normalTextarea">
        <div class="playbook-inline-header">
            <button class="playbook-inline-dismiss" type="button" (click)="dismissPlaybook()" title="Cancel playbook">
                <i class="fa-regular fa-xmark"></i>
            </button>
            <span class="playbook-inline-title">{{ activePlaybook.title }}</span>
        </div>
        <p class="playbook-inline-prompt">
            <ng-container *ngFor="let part of activePlaybook.promptParts">
                <span *ngIf="part.type === 'text'" class="playbook-inline-text">{{ part.value }}</span>
                <ng-container *ngIf="part.type === 'field'">
                    <ng-container *ngIf="getFieldDef(part.value) as field">
                        <!-- Text input -->
                        <input *ngIf="field.type === 'text'"
                               class="playbook-inline-input"
                               type="text"
                               [placeholder]="field.placeholder || field.label"
                               [attr.aria-label]="field.label"
                               [attr.size]="getInputSize(field)"
                               [value]="playbookFieldValues[field.id] || ''"
                               (input)="onPlaybookFieldInput(field.id, $event)"
                               (keydown.enter)="onPlaybookEnter($event)" />

                        <!-- Select (trigger button that opens modal) -->
                        <button *ngIf="field.type === 'select' || field.type === 'multi-select'"
                                type="button"
                                class="playbook-inline-select-trigger"
                                (click)="openFieldSelectModal(field)"
                                [attr.aria-label]="field.label">
                            <img *ngIf="getSelectedOptionIcon(field.id) as iconUrl" [src]="iconUrl" alt="" class="playbook-select-icon" />
                            <span [class.is-placeholder]="!playbookFieldValues[field.id]">{{ getSelectedOptionLabel(field.id) || field.placeholder || field.label }}</span>
                            <svg class="playbook-select-chevron" width="8" height="5" viewBox="0 0 8 5" fill="none"><path d="M1 1l3 3 3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </button>

                        <!-- Context field (trigger button) -->
                        <button *ngIf="field.type === 'context'"
                                type="button"
                                class="playbook-inline-select-trigger playbook-context-trigger"
                                (click)="openContextFieldModal(field)"
                                [attr.aria-label]="field.label">
                            <i class="fa-regular" [class.fa-building]="field.contextType === 'business'" [class.fa-file-lines]="field.contextType === 'proposal'"></i>
                            <span [class.is-placeholder]="!playbookFieldValues[field.id]">{{ playbookFieldValues[field.id] || field.defaultValue || field.placeholder || field.label }}</span>
                            <svg class="playbook-select-chevron" width="8" height="5" viewBox="0 0 8 5" fill="none"><path d="M1 1l3 3 3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </button>
                    </ng-container>
                </ng-container>
            </ng-container>
            <button *ngIf="activePlaybook.systemPromptOverride" type="button" class="playbook-system-prompt-link" (click)="openSystemPromptModal()" title="View system prompt">
                <i class="fa-regular fa-terminal"></i>
            </button>
        </p>
    </div>

    <!-- Normal textarea -->
    <ng-template #normalTextarea>
        <div class="composer-input-row">
            <div class="composer-skill-overlay"
                 *ngIf="hasSkillChipsInMessage"
                 aria-hidden="true">
                <ng-container *ngFor="let part of composerSkillParts">
                    <span *ngIf="part.type === 'text'" class="composer-skill-overlay-text">{{ part.value }}</span>
                    <span *ngIf="part.type === 'skill'" class="composer-skill-token-shell">
                        <span class="composer-skill-token-placeholder">/{{ part.value }}</span>
                        <span class="composer-skill-chip">
                            <span>{{ part.value }}</span>
                        </span>
                    </span>
                </ng-container>
            </div>
            <textarea #chatTextarea
                      class="composer-textarea"
                      [class.composer-textarea--skill-overlay]="hasSkillChipsInMessage"
                      [placeholder]="placeholderText"
                      [value]="newMessage"
                      (input)="onInputChange($event)"
                      (paste)="onTextareaPaste($event)"
                      (keydown)="onTextareaKeyDown($event)"
                      [disabled]="isLoadingOrInitializing"
                      rows="1"
                      style="height:24px"></textarea>

            <span class="composer-inline-local-hint"
                  *ngIf="showInlineLocalBackendIndicator"
                  [attr.title]="localBackendIndicatorTitle"
                  aria-label="Local backend active">Local</span>
        </div>
    </ng-template>

    <!-- Bottom toolbar -->
    <div class="composer-toolbar">
        <div class="composer-toolbar-left" #toolbarLeft>
            <!-- Status chips -->
            <div class="composer-toolbar-leading" #toolbarLeading>
                <button class="composer-chip composer-chip--agent" *ngIf="selectedAgentOption as currentAgentOption; else legacyAgentChip" type="button" (click)="$event.stopPropagation(); onAgentChipClick()" [attr.title]="(currentAgentOption.name || currentAgentOption.label) + (currentAgentOption.name ? ' · ' + currentAgentOption.label : '')">
                    <span class="composer-chip-agent-avatar" [style]="currentAgentOption.portraitStyle"></span>
                    <span class="composer-chip-agent-name">{{ currentAgentOption.name || currentAgentOption.label }}</span>
                </button>
                <ng-template #legacyAgentChip>
                    <button class="composer-chip" *ngIf="agentLabel" type="button" (click)="$event.stopPropagation(); onAgentChipClick()">{{ agentLabel }}</button>
                </ng-template>
                <button class="composer-chip composer-chip--model" type="button" (click)="$event.stopPropagation(); onModelChipClick()">
                    <span class="composer-chip-model-avatar" *ngIf="currentModelAvatar" [ngStyle]="currentModelBadgeStyle">{{ currentModelAvatar }}</span>
                    <span>{{ modelLabel || 'Auto' }}</span>
                </button>
                <button class="composer-chip composer-chip--credits"
                        *ngIf="shouldShowCreditChip"
                        type="button"
                        (click)="$event.stopPropagation(); addCreditsRequested.emit()"
                        [class.is-empty]="isCreditDepleted"
                        [attr.title]="creditChipTitle"
                        aria-label="View agency credits">
                    <i class="fa-solid fa-bolt"></i>
                    <span>{{ creditChipLabel }}</span>
                </button>
            </div>

            <div class="composer-toolbar-attachments" *ngIf="totalComposerAttachmentCount > 0">
                <ng-container *ngIf="!shouldGroupComposerAttachments; else groupedComposerAttachments">
                    <div class="composer-attachment-chip composer-attachment-chip--context"
                         *ngFor="let item of inlineComposerAttachmentItems"
                         [attr.title]="getComposerAttachmentTitle(item)">
                        <span class="composer-attachment-chip-icon" aria-hidden="true">
                            <span class="composer-attachment-chip-spinner" *ngIf="item.status === 'uploading'"></span>
                            <i class="fa-regular fa-circle-exclamation" *ngIf="item.status === 'error'"></i>
                            <i [class]="item.iconClass" *ngIf="item.status === 'ready'"></i>
                        </span>
                        <span class="composer-attachment-chip-copy">
                            <span class="composer-attachment-chip-title">{{ item.label }}</span>
                        </span>
                        <button class="composer-attachment-chip-remove"
                                type="button"
                                (click)="$event.stopPropagation(); onUnifiedAttachmentRemoved(item)"
                                [attr.aria-label]="'Remove ' + item.label">
                            <i class="fa-solid fa-xmark"></i>
                        </button>
                    </div>
                </ng-container>

                <ng-template #groupedComposerAttachments>
                    <button class="composer-chip composer-chip--context composer-chip--context-group composer-chip--attachments-group"
                            type="button"
                            (click)="openAttachedPopover($event)">
                        <i class="fa-regular fa-paperclip"></i>
                        <span class="composer-chip-label">Attachments {{ totalComposerAttachmentCount }}</span>
                    </button>
                </ng-template>
            </div>

        </div>

        <div class="composer-toolbar-right">
            <button class="composer-btn composer-btn--more"
                    type="button"
                    (click)="onMoreOptionsClick()"
                    [class.is-active]="activeSelectorModal === 'more'"
                    title="More options"
                    aria-label="Open more options">
                <i class="fa-solid fa-ellipsis"></i>
            </button>

            <button class="composer-context-usage"
                    *ngIf="shouldShowContextUsageButton"
                    type="button"
                    (click)="onContextUsageClick()"
                    [class.is-active]="activeSelectorModal === 'contextUsage'"
                    [ngClass]="contextUsageLevelClass"
                    [ngStyle]="contextUsageRingStyle"
                    [attr.title]="contextUsageButtonTitle"
                    [attr.aria-label]="'Open context window details. ' + contextUsageButtonTitle">
            </button>

            <button class="composer-btn composer-btn--add"
                    type="button"
                    (click)="onAddClick()"
                    [class.is-active]="activeSelectorModal === 'add'"
                    [disabled]="isLoadingOrInitializing"
                    title="Add attachment">
                <i class="fa-regular fa-plus"></i>
            </button>

            <!-- Transcription processing (replaces mic button only) -->
            <div class="composer-transcribing" *ngIf="showMicrophoneAction && isProcessingTranscription">
                <div class="composer-transcribing-wave">
                    <span></span><span></span><span></span><span></span>
                </div>
                <button class="composer-btn composer-btn--cancel"
                        *ngIf="showTranscriptionCancel"
                        type="button"
                        (click)="transcriptionCancelled.emit()">
                    <i class="fa-regular fa-xmark"></i>
                </button>
            </div>

            <!-- Mic / recording (hidden while transcribing) -->
            <button class="composer-btn composer-btn--mic"
                    *ngIf="showMicrophoneAction && !isProcessingTranscription"
                    type="button"
                    (click)="toggleMicrophone()"
                    [class.is-recording]="isRecording"
                    [class.is-connecting]="isConnecting"
                    [disabled]="isLoadingOrInitializing">
                <i class="fa-regular fa-microphone" *ngIf="!isRecording && !isConnecting"></i>
                <i class="fa-solid fa-circle-notch fa-spin" *ngIf="isConnecting"></i>
                <i class="fa-regular fa-stop" *ngIf="isRecording"></i>
            </button>

            <button class="composer-btn composer-btn--stop"
                    *ngIf="showSendAction && shouldShowStopAction; else sendButtonTemplate"
                    type="button"
                    (click)="stopCurrentThread()"
                    title="Stop"
                    aria-label="Stop current task">
                <i class="fa-solid fa-stop"></i>
            </button>

            <ng-template #sendButtonTemplate>
                <button class="composer-btn composer-btn--send"
                        *ngIf="showSendAction"
                        type="button"
                        (click)="activePlaybook ? submitPlaybook() : sendMessage()"
                        [disabled]="!canSendCurrentMessage">
                    <i class="fa-regular fa-arrow-up" *ngIf="!isLoadingOrInitializing"></i>
                    <div class="composer-spinner" *ngIf="isLoadingOrInitializing"></div>
                </button>
            </ng-template>
        </div>
    </div>
</div>

    <div class="composer-suggestions-section composer-suggestions-section--below" *ngIf="shouldShowSuggestionsBelow">
        <div class="composer-suggestions-label">Suggested prompts</div>
        <div class="composer-suggestions">
            <button class="composer-suggestion"
                    *ngFor="let playbook of contextPrompts"
                    type="button"
                    (click)="onContextPromptClick(playbook)"
                    [attr.aria-label]="'Use playbook ' + playbook.title">
                <i class="fa-regular {{ playbook.icon }} composer-suggestion-icon"></i>
                <span>{{ playbook.title }}</span>
            </button>
        </div>
    </div>
</div>

<ng-template #attachmentPopoverTemplate>
    <div class="attached-popover" (click)="$event.stopPropagation()">
        <div class="attached-popover-item" *ngFor="let item of allComposerAttachmentItems">
            <i [class]="item.iconClass + ' attached-popover-icon'"></i>
            <span class="attached-popover-name">{{ item.label }}</span>
            <button class="attached-popover-remove" type="button"
                    (click)="$event.stopPropagation(); onUnifiedAttachmentRemoved(item)"
                    [attr.aria-label]="'Remove ' + item.label">
                <i class="fa-solid fa-xmark"></i>
            </button>
        </div>
    </div>
</ng-template>

<!-- Add Menu Popover -->
<ng-template #addMenuPopoverTemplate>
    <div class="add-menu-popover" [class.light-mode]="theme.isLight">
        <button class="add-menu-item"
                type="button"
                *ngFor="let option of addMenuOptions; let i = index; trackBy: trackAddMenuOption"
                (click)="onAddMenuAction(option.value)">
            <i [class]="option.iconClass"></i>
            <span class="add-menu-item-copy">
                <span class="add-menu-item-title">{{ option.label }}</span>
                <span class="add-menu-item-description">{{ option.description }}</span>
            </span>
            <span class="add-menu-item-meta-group">
                <span class="add-menu-item-shortcut">{{ i + 1 }}</span>
            </span>
        </button>
    </div>
</ng-template>

<ng-template #moreMenuPopoverTemplate>
    <div class="add-menu-popover more-menu-popover" [class.light-mode]="theme.isLight">
        <button class="add-menu-item"
                type="button"
                *ngFor="let option of moreMenuOptions; let i = index; trackBy: trackMoreMenuOption"
                [disabled]="option.disabled"
                [class.is-disabled]="option.disabled"
                (click)="onMoreMenuAction(option.value)">
            <i [class]="option.iconClass"></i>
            <span class="add-menu-item-copy">
                <span class="add-menu-item-title">{{ option.label }}</span>
                <span class="add-menu-item-description">{{ option.badgeText ? option.badgeText + ' · ' : '' }}{{ option.description }}</span>
            </span>
            <span class="add-menu-item-meta-group">
                <span class="add-menu-item-shortcut">{{ i + 1 }}</span>
            </span>
        </button>
    </div>
</ng-template>

<ng-template #connectorsModalTemplate>
    <app-chat-connectors-modal variant="chat"></app-chat-connectors-modal>
</ng-template>

<ng-template #devicesModalTemplate>
    <app-chat-devices-modal [initialCommandTarget]="deviceCommandModalTarget"></app-chat-devices-modal>
</ng-template>

<ng-template #skillsModalTemplate>
    <div class="skills-modal"
         [class.light-mode]="theme.isLight"
         tabindex="0"
         (keydown)="onSkillsModalKeyDown($event)">
        <div class="skills-modal-empty" *ngIf="!availableSkills.length">
            <i class="fa-regular fa-folder-open"></i>
            <span>No agency skills saved yet</span>
        </div>

        <button
            class="skills-modal-item"
            type="button"
            *ngFor="let skill of availableSkills; let i = index"
            [class.is-selected]="i === selectedSkillIndex"
            (mouseenter)="selectedSkillIndex = i"
            (click)="onSkillSelected(skill)">
            <i class="fa-regular fa-wand-magic-sparkles"></i>
            <span class="skills-modal-copy">
                <span class="skills-modal-title">/{{ skill.name }}</span>
                <span class="skills-modal-description">{{ skill.description }}</span>
            </span>
        </button>
    </div>
</ng-template>

<!-- Agent Selector Popover -->
<ng-template #agentPopoverTemplate>
    <div class="popover-list" [class.light-mode]="theme.isLight" (click)="$event.stopPropagation()">
        <div class="popover-list-item popover-list-item--agent"
             *ngFor="let agentOption of availableAgents"
             [class.active]="agentOption.value === agent"
             (click)="selectAgentFromPopover(agentOption)">
            <div class="agent-option-avatar" [style]="agentOption.portraitStyle"></div>
            <div class="popover-list-item-content popover-list-item-content--agent">
                <span class="popover-list-item-name">{{ agentOption.name || agentOption.label }}</span>
                <small class="popover-list-item-desc" *ngIf="agentOption.name">{{ agentOption.label }}</small>
            </div>
        </div>
    </div>
</ng-template>

<!-- Mode Selector Popover -->
<ng-template #modePopoverTemplate>
    <div class="view-modal" [class.light-mode]="theme.isLight" (click)="$event.stopPropagation()">
        <div class="view-modal-section">
            <span class="view-modal-section-title">Conversation mode</span>

            <button type="button"
                    class="view-modal-item"
                    *ngFor="let modeOption of modeOptions"
                    [class.is-active]="currentMode === modeOption.value"
                    (click)="selectModeFromPopover(modeOption)">
                <i class="fas fa-question-circle" *ngIf="modeOption.value === 'ask'"></i>
                <i class="fas fa-robot" *ngIf="modeOption.value === 'agent'"></i>
                <span class="view-modal-item-copy">
                    <span class="view-modal-item-title">{{ modeOption.label }}</span>
                    <span class="view-modal-item-description" *ngIf="modeOption.value === 'ask'">Single response</span>
                    <span class="view-modal-item-description" *ngIf="modeOption.value === 'agent'">Multi-step agent</span>
                </span>
                <i class="fa-solid fa-check view-modal-item-check" *ngIf="currentMode === modeOption.value"></i>
            </button>
        </div>

        <div class="view-modal-section" *ngIf="isLocalDevelopment">
            <span class="view-modal-section-title">Backend target</span>

            <button type="button"
                    class="view-modal-item"
                    [class.is-active]="useLocalBackend"
                    (click)="onThreadBackendSelected(true)">
                <i class="fa-solid fa-laptop-code"></i>
                <span class="view-modal-item-copy">
                    <span class="view-modal-item-title">Local</span>
                    <span class="view-modal-item-description">Route this thread to the local agent backend.</span>
                </span>
                <i class="fa-solid fa-check view-modal-item-check" *ngIf="useLocalBackend"></i>
            </button>

            <button type="button"
                    class="view-modal-item"
                    [class.is-active]="!useLocalBackend"
                    (click)="onThreadBackendSelected(false)">
                <i class="fa-solid fa-cloud"></i>
                <span class="view-modal-item-copy">
                    <span class="view-modal-item-title">{{ hostedBackendLabel }}</span>
                    <span class="view-modal-item-description">Use the hosted backend instead of localhost.</span>
                </span>
                <i class="fa-solid fa-check view-modal-item-check" *ngIf="!useLocalBackend"></i>
            </button>
        </div>
    </div>
</ng-template>

<ng-template #selectorModalHeaderTemplate>
    <div class="selector-modal-header" [class.light-mode]="theme.isLight">
        <div class="selector-modal-header-copy">
            <div class="selector-modal-header-title-row">
                <h3 class="modal-heading">{{ activeSelectorModalHeaderTitle }}</h3>
                <span class="modal-shortcut-hint"
                      *ngIf="activeSelectorModalHeaderShortcut as hint"
                      [attr.aria-label]="hint.ariaLabel"
                      [attr.title]="hint.ariaLabel">
                    <kbd>{{ hint.label }}</kbd>
                </span>
            </div>
            <p class="selector-modal-subtitle" *ngIf="activeSelectorModalHeaderSubtitle">{{ activeSelectorModalHeaderSubtitle }}</p>
        </div>
    </div>
</ng-template>

<ng-template #modelHeaderTemplate>
    <div class="model-modal-header" [class.light-mode]="theme.isLight">
        <div class="model-modal-header-copy">
            <div class="model-modal-header-title-row">
                <h3 class="modal-heading">Models</h3>
                <span class="modal-shortcut-hint"
                      *ngIf="modelShortcutHint as hint"
                      [attr.aria-label]="hint.ariaLabel"
                      [attr.title]="hint.ariaLabel">
                    <kbd>{{ hint.label }}</kbd>
                </span>
            </div>

            <div
                *ngIf="displayModelCatalogStatus !== 'loading' && activeModelSettingsTab === 'default'"
                class="model-modal-header-secondary">
                <div class="model-browser-segment model-browser-segment--secondary" role="tablist" aria-label="Default model grouping">
                    <button
                        *ngFor="let option of recommendedGroupingOptions"
                        type="button"
                        class="model-browser-segment-option"
                        [class.active]="recommendedGroupingMode === option.value"
                        (click)="setRecommendedGroupingMode(option.value)">
                        {{ option.label }}
                    </button>
                </div>

                <div class="model-blend-control" *ngIf="isRecommendedSortEnabled" [class.is-open]="isPriceBlendSelectorOpen">
                    <button
                        type="button"
                        class="model-blend-toggle"
                        [attr.aria-expanded]="isPriceBlendSelectorOpen"
                        aria-haspopup="true"
                        [attr.aria-label]="'Price blend preset ' + activePriceBlendPreset.ratioLabel"
                        [attr.title]="'Blended price preset: ' + activePriceBlendPreset.ratioLabel"
                        (click)="togglePriceBlendSelector($event)">
                        <i class="fa-regular fa-sliders" aria-hidden="true"></i>
                        <span>{{ activePriceBlendPreset.ratioLabel }}</span>
                    </button>

                    <div class="model-blend-panel" *ngIf="isPriceBlendSelectorOpen" (click)="$event.stopPropagation()" role="radiogroup" aria-label="Price blend presets">
                        <label class="model-blend-option" *ngFor="let preset of priceBlendPresets">
                            <input
                                type="radio"
                                name="model-price-blend"
                                [checked]="priceBlendPresetKey === preset.key"
                                (change)="setPriceBlendPreset(preset.key, $event)" />
                            <span class="model-blend-option-copy">
                                <span class="model-blend-option-ratio">{{ preset.ratioLabel }}</span>
                                <span class="model-blend-option-label">{{ preset.label }}</span>
                            </span>
                        </label>
                    </div>
                </div>

                <button
                    *ngIf="isRecommendedSortEnabled"
                    type="button"
                    class="model-sort-toggle"
                    [attr.aria-label]="getRecommendedSortLabel()"
                    [attr.title]="getRecommendedSortLabel()"
                    (click)="toggleRecommendedSortDirection()">
                    <i
                        class="fa-regular"
                        [class.fa-arrow-down]="recommendedSortDirection === 'asc'"
                        [class.fa-arrow-up]="recommendedSortDirection === 'desc'"
                        aria-hidden="true"></i>
                </button>
            </div>
        </div>
    </div>
</ng-template>

<ng-template #modelHeaderActionsTemplate>
    <div class="model-modal-header-actions" [class.light-mode]="theme.isLight">
        <div class="model-browser-segment model-browser-segment--header" role="tablist" aria-label="Model defaults tabs">
            <button
                *ngFor="let tab of modelSettingsTabs"
                type="button"
                class="model-browser-segment-option"
                [class.active]="activeModelSettingsTab === tab.key"
                (click)="setActiveModelSettingsTab(tab.key)">
                {{ tab.label }}
            </button>
        </div>
    </div>
</ng-template>

<ng-template #modelPopoverTemplate>
    <div class="popover-list popover-list--models" [class.light-mode]="theme.isLight" (click)="onModelPopoverBodyClick($event)">
        <div class="model-browser-loading" *ngIf="displayModelCatalogStatus === 'loading'">
            <div class="model-browser-spinner" aria-hidden="true"></div>
            <div class="model-browser-loading-copy">
                <strong>Loading live model catalog</strong>
                <span>{{ getModelCatalogBodyMessage() }}</span>
            </div>
        </div>

        <ng-container *ngIf="activeModelSettingsTab === 'default'; else mediaSpecificCatalog">
            <ng-container *ngIf="recommendedGroupingMode === 'provider'; else curatedRecommendedList">
                <div class="model-group" *ngFor="let group of visibleGroupedAvailableModels" [hidden]="displayModelCatalogStatus === 'loading'">
                    <button class="model-group-header model-group-header--accordion" type="button" (click)="toggleProvider(group.key, $event)">
                        <div class="model-group-provider-lockup">
                            <div class="model-provider-avatar" [ngStyle]="group.badgeStyle">
                                {{ group.avatar }}
                            </div>
                            <div class="model-group-provider-copy">
                                <div class="model-group-title-row">
                                    <span class="model-group-title">{{ group.label }}</span>
                                    <span class="model-group-count">{{ group.count }}</span>
                                </div>
                                <small class="model-group-description">{{ group.description }}</small>
                            </div>
                        </div>
                        <i class="far" [class.fa-chevron-down]="isProviderExpanded(group.key)" [class.fa-chevron-right]="!isProviderExpanded(group.key)"></i>
                    </button>

                    <div class="model-group-panel" [class.is-expanded]="isProviderExpanded(group.key)">
                        <div class="model-group-panel-inner">
                            <div class="model-subgroup" *ngIf="group.recommendedEntries.length > 0">
                                <div class="model-subgroup-label">Recommended</div>
                                <div class="popover-list-item popover-list-item--model"
                                     *ngFor="let entry of group.recommendedEntries"
                                     [class.active]="entry.model.key === currentModelKey"
                                     [class.is-disabled]="!entry.selectable"
                                     (click)="selectModelFromPopover(entry.model)">
                                    <div class="model-provider-avatar" [ngStyle]="entry.badgeStyle">
                                        {{ entry.avatar }}
                                    </div>

                                    <div class="popover-list-item-content popover-list-item-content--model">
                                        <div class="model-title-row">
                                            <div class="model-title-main">
                                                <span class="popover-list-item-name">{{ entry.model.displayName }}</span>
                                                <span class="model-tier-pill">{{ entry.tier }}</span>
                                            </div>
                                            <button
                                                type="button"
                                                class="model-info-chip"
                                                (click)="onModelInfoClick($event, entry.tooltipText)"
                                                aria-label="Model details">
                                                <i class="fa-regular fa-circle-info"></i>
                                                <span>Info</span>
                                            </button>
                                        </div>
                                        <small class="popover-list-item-desc">{{ entry.summary }} · {{ entry.secondaryText }}</small>
                                        <div class="model-price-row">{{ entry.pricingText }}</div>
                                        <div class="model-badges">
                                            <span class="model-badge" *ngFor="let badge of entry.badges">
                                                {{ badge }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="model-subgroup" *ngIf="group.additionalEntries.length > 0">
                                <div class="model-subgroup-label">Full Catalog</div>
                                <button class="model-option-chip"
                                     *ngFor="let entry of group.additionalEntries"
                                     type="button"
                                     [class.active]="entry.model.key === currentModelKey"
                                     [class.is-disabled]="!entry.selectable"
                                     (click)="selectModelFromPopover(entry.model)">
                                    <span class="model-option-chip-name">{{ entry.model.displayName }}</span>
                                    <span class="model-option-chip-price">{{ entry.compactPricingText }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ng-container>

            <ng-template #curatedRecommendedList>
                <div class="model-curated-list" *ngIf="displayModelCatalogStatus !== 'loading'">
                    <button class="popover-list-item popover-list-item--model popover-list-item--recommended-list"
                         *ngFor="let entry of sortedRecommendedModelEntries"
                         type="button"
                         [class.active]="entry.model.key === currentModelKey"
                         [class.is-disabled]="!entry.selectable"
                         (click)="selectModelFromPopover(entry.model)">
                        <div class="model-provider-avatar" [ngStyle]="entry.badgeStyle">
                            {{ entry.avatar }}
                        </div>

                        <div class="popover-list-item-content popover-list-item-content--model">
                            <div class="model-title-row">
                                <div class="model-title-main">
                                    <span class="popover-list-item-name">{{ entry.model.displayName }}</span>
                                    <span class="model-tier-pill">{{ entry.tier }}</span>
                                </div>
                                <button
                                    type="button"
                                    class="model-info-chip"
                                    (click)="onModelInfoClick($event, entry.tooltipText)"
                                    aria-label="Model details">
                                    <i class="fa-regular fa-circle-info"></i>
                                    <span>Info</span>
                                </button>
                            </div>
                            <small class="popover-list-item-desc">{{ entry.meta }} · {{ entry.secondaryText }}</small>
                            <div class="model-price-row">{{ entry.pricingText }}</div>
                            <div class="model-badges">
                                <span class="model-badge" *ngFor="let badge of entry.badges">
                                    {{ badge }}
                                </span>
                            </div>
                        </div>
                    </button>
                </div>
            </ng-template>
        </ng-container>

        <ng-template #mediaSpecificCatalog>
            <div class="model-curated-list" *ngIf="displayModelCatalogStatus !== 'loading'">
                <div *ngIf="activeModelSettingsTab === 'audio'"
                     class="popover-list-item popover-list-item--model popover-list-item--recommended-list model-media-row"
                     [class.active]="isSystemDefaultMediaSelectionActive('audio')"
                     tabindex="0"
                     role="button"
                     (click)="resetAudioPreferencesToDefault()"
                     (keydown.enter)="resetAudioPreferencesToDefault()">
                    <div class="model-provider-avatar model-provider-avatar--default">Df</div>

                    <div class="popover-list-item-content popover-list-item-content--model">
                        <div class="model-title-row">
                            <div class="model-title-main">
                                <span class="popover-list-item-name">Use system default</span>
                                <span class="model-tier-pill">Audio</span>
                            </div>
                        </div>
                        <small class="popover-list-item-desc">{{ audioSystemDefaultDescription }}</small>
                        <div class="model-voice-chip-row" (click)="$event.stopPropagation()">
                            <button
                                type="button"
                                class="model-voice-chip"
                                *ngFor="let option of getAudioVoiceOptionsForModel('')"
                                [class.active]="isAudioVoiceOptionSelected('', option.value)"
                                (click)="onAudioVoiceChipSelected($event, '', option.value)">
                                {{ option.label }}
                            </button>
                        </div>
                    </div>
                </div>

                <div *ngIf="activeModelSettingsTab === 'image'"
                     class="popover-list-item popover-list-item--model popover-list-item--recommended-list model-media-row"
                     [class.active]="isSystemDefaultMediaSelectionActive('image')"
                     tabindex="0"
                     role="button"
                     (click)="resetImagePreferenceToDefault()"
                     (keydown.enter)="resetImagePreferenceToDefault()">
                    <div class="model-provider-avatar model-provider-avatar--default">Df</div>

                    <div class="popover-list-item-content popover-list-item-content--model">
                        <div class="model-title-row">
                            <div class="model-title-main">
                                <span class="popover-list-item-name">Use system default</span>
                                <span class="model-tier-pill">Image</span>
                            </div>
                        </div>
                        <small class="popover-list-item-desc">{{ imageSystemDefaultDescription }}</small>
                    </div>
                </div>

                <div *ngIf="activeModelSettingsTab === 'video'"
                     class="popover-list-item popover-list-item--model popover-list-item--recommended-list model-media-row"
                     [class.active]="isSystemDefaultMediaSelectionActive('video')"
                     tabindex="0"
                     role="button"
                     (click)="resetVideoPreferenceToDefault()"
                     (keydown.enter)="resetVideoPreferenceToDefault()">
                    <div class="model-provider-avatar model-provider-avatar--default">Df</div>

                    <div class="popover-list-item-content popover-list-item-content--model">
                        <div class="model-title-row">
                            <div class="model-title-main">
                                <span class="popover-list-item-name">Use system default</span>
                                <span class="model-tier-pill">Video</span>
                            </div>
                        </div>
                        <small class="popover-list-item-desc">{{ videoSystemDefaultDescription }}</small>
                    </div>
                </div>

                <div class="popover-list-item popover-list-item--model popover-list-item--recommended-list model-media-row"
                     *ngFor="let model of activeMediaCatalogModels"
                     [class.active]="model.key === selectedMediaModelKeyForActiveTab"
                     tabindex="0"
                     role="button"
                     (click)="selectModelPreferenceFromActiveTab(model)"
                     (keydown.enter)="selectModelPreferenceFromActiveTab(model)">
                    <div class="model-provider-avatar" [ngStyle]="getProviderBadgeStyle(model)">
                        {{ getProviderAvatar(model) }}
                    </div>

                    <div class="popover-list-item-content popover-list-item-content--model">
                        <div class="model-title-row">
                            <div class="model-title-main">
                                <span class="popover-list-item-name">{{ model.displayName }}</span>
                                <span class="model-tier-pill">{{ activeModelSettingsTab | titlecase }}</span>
                            </div>
                            <button
                                type="button"
                                class="model-info-chip"
                                (click)="onModelInfoClick($event, getModelTooltipText(model))"
                                aria-label="Model details">
                                <i class="fa-regular fa-circle-info"></i>
                                <span>Info</span>
                            </button>
                        </div>
                        <small class="popover-list-item-desc">{{ getProviderLabel(model.provider || model.category) }} · {{ getModelSecondaryText(model) }}</small>
                        <div class="model-price-row">{{ getModelPricingText(model) }}</div>
                        <div class="model-badges">
                            <span class="model-badge" *ngFor="let badge of getModelBadges(model)">
                                {{ badge }}
                            </span>
                        </div>

                        <div class="model-voice-chip-row" *ngIf="activeModelSettingsTab === 'audio'" (click)="$event.stopPropagation()">
                            <button
                                type="button"
                                class="model-voice-chip"
                                *ngFor="let option of getAudioVoiceOptionsForModel(model.key)"
                                [class.active]="isAudioVoiceOptionSelected(model.key, option.value)"
                                (click)="onAudioVoiceChipSelected($event, model.key, option.value)">
                                {{ option.label }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </ng-template>

        <div class="model-browser-empty"
             [class.model-browser-empty--error]="displayModelCatalogStatus === 'error'"
             *ngIf="activeModelSettingsTab === 'default' && groupedAvailableModels.length === 0 && displayModelCatalogStatus !== 'loading'">
            {{ getModelCatalogBodyMessage() }}
        </div>

        <div class="model-browser-empty"
             *ngIf="activeModelSettingsTab === 'default' && recommendedGroupingMode === 'provider' && visibleGroupedAvailableModels.length === 0 && displayModelCatalogStatus !== 'loading' && groupedAvailableModels.length > 0">
            No recommended models are available in the provider view right now.
        </div>

        <div class="model-browser-empty"
             *ngIf="activeModelSettingsTab === 'default' && recommendedGroupingMode === 'all' && sortedRecommendedModelEntries.length === 0 && displayModelCatalogStatus !== 'loading' && groupedAvailableModels.length > 0">
            No recommended models are available in the live catalog right now.
        </div>

        <div class="model-browser-empty"
             *ngIf="activeModelSettingsTab !== 'default' && activeMediaCatalogModels.length === 0 && displayModelCatalogStatus !== 'loading'">
            {{ mediaCatalogEmptyMessage }}
        </div>
    </div>
</ng-template>

<ng-template #toolPopoverTemplate>
    <div class="popover-list popover-list--tools" [class.light-mode]="theme.isLight" (click)="$event.stopPropagation()">
        <div class="model-browser-loading" *ngIf="displayToolCatalogStatus === 'loading'">
            <div class="model-browser-spinner" aria-hidden="true"></div>
            <div class="model-browser-loading-copy">
                <strong>Loading live tool catalog</strong>
                <span>{{ getToolCatalogBodyMessage() }}</span>
            </div>
        </div>

        <div class="tool-selection-summary" *ngIf="displayToolCatalogStatus !== 'loading'">
            <div class="tool-selection-summary-header">
                <strong>{{ selectedToolCount > 0 ? selectedToolCount + ' selected tools' : 'All tools enabled' }}</strong>
                <div class="tool-selection-summary-actions" *ngIf="selectedToolCount > 0 || (hasDefaultToolSelection && !isUsingDefaultToolSelection)">
                    <button type="button"
                            class="tool-selection-summary-action"
                            *ngIf="hasDefaultToolSelection && !isUsingDefaultToolSelection"
                            (click)="applyDefaultToolSelection()">
                        Default
                    </button>
                    <button type="button"
                            class="tool-selection-summary-action tool-selection-summary-action--icon"
                            *ngIf="selectedToolCount > 0"
                            (click)="clearToolSelection()"
                            title="Use all tools">
                        <i class="fa-regular fa-xmark"></i>
                    </button>
                </div>
            </div>
            <span>{{ getToolCatalogBodyMessage() }}</span>
        </div>

        <div class="tool-groups" *ngIf="displayToolCatalogStatus !== 'loading' && groupedAvailableTools.length > 0">
            <div class="model-group tool-group" *ngFor="let group of groupedAvailableTools; trackBy: trackToolGroup">
                <button class="model-group-header model-group-header--accordion tool-group-header"
                        type="button"
                        [attr.aria-expanded]="isToolGroupExpanded(group.key)"
                        (click)="toggleToolGroup(group.key, $event)">
                    <div class="model-group-provider-lockup">
                        <div class="model-group-provider-copy">
                            <div class="model-group-title-row">
                                <span class="model-group-title">{{ group.label }}</span>
                                <div class="tool-group-badges">
                                    <span class="tool-group-selected-pill" *ngIf="group.selectedCount > 0">{{ group.selectedCount }} selected</span>
                                    <span class="model-group-count">{{ group.tools.length }}</span>
                                </div>
                            </div>
                            <span class="model-group-description">{{ group.description }}</span>
                        </div>
                    </div>
                    <i class="fa-regular" [ngClass]="isToolGroupExpanded(group.key) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </button>

                <div class="model-group-panel" [class.is-expanded]="isToolGroupExpanded(group.key)">
                    <div class="model-group-panel-inner tool-group-panel-inner">
                        <button class="popover-list-item popover-list-item--tool"
                                type="button"
                                *ngFor="let tool of group.tools; trackBy: trackToolOption"
                                [class.active]="isToolSelected(tool.name)"
                                (click)="toggleToolSelection(tool)">
                            <div class="tool-toggle-indicator" [class.is-active]="isToolSelected(tool.name)">
                                <i class="fa-solid fa-check" *ngIf="isToolSelected(tool.name)"></i>
                            </div>
                            <div class="popover-list-item-content popover-list-item-content--tool">
                                <div class="tool-title-row">
                                    <span class="popover-list-item-name">{{ getToolDisplayName(tool) }}</span>
                                </div>
                                <small class="popover-list-item-desc">{{ tool.description }}</small>
                                <div class="tool-meta-row" *ngIf="getToolMeta(tool)">{{ getToolMeta(tool) }}</div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="model-browser-empty"
             [class.model-browser-empty--error]="displayToolCatalogStatus === 'error'"
             *ngIf="displayToolCatalogStatus !== 'loading' && groupedAvailableTools.length === 0">
            {{ getToolCatalogBodyMessage() }}
        </div>
    </div>
</ng-template>

<!-- Prompt Popover -->
<ng-template #promptPopoverTemplate>
    <app-prompt-popover
        [embedded]="true"
        [showPromptPopover]="showPromptPopover"
        [availablePlaybooks]="availablePlaybooks"
        [selectedPromptIndex]="selectedPromptIndex"
        [agent]="agent"
        (playbookSelected)="onPlaybookSelectedFromPopover($event)"
        (selectedIndexChanged)="selectedPromptIndex = $event"
        (popoverClosed)="closeSelectorModal()">
    </app-prompt-popover>
</ng-template>

<!-- Context Reference Popover -->
<ng-template #contextPopoverTemplate>
    <app-context-reference-popover #contextPopoverRef
        [embedded]="true"
        [showContextPopover]="showContextPopover"
        [contextItems]="contextItems"
        [selectedContextIndex]="selectedContextIndex"
        [contextUserObject]="contextUserObject"
        [agencyObject]="agencyObject"
        [initialSource]="contextPickerInitialSource"
        [preselectedBusinessUid]="contextUid"
        (contextItemSelected)="onContextItemSelectedFromPopover($event)"
        (selectedIndexChanged)="selectedContextIndex = $event"
        (popoverClosed)="closeSelectorModal()">
    </app-context-reference-popover>
</ng-template>

<!-- Context Popover Footer -->
<ng-template #contextFooterTemplate>
    <div class="context-footer-shortcuts" *ngIf="contextPopoverRef?.view === 'roots'">
        <span class="context-footer-shortcut"><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
        <span class="context-footer-shortcut"><kbd>↵</kbd> Open or attach</span>
        <span class="context-footer-shortcut"><kbd>⇧</kbd><kbd>↵</kbd> Attach</span>
    </div>
    <div class="context-footer-shortcuts" *ngIf="contextPopoverRef?.view === 'items'">
        <span class="context-footer-shortcut"><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
        <span class="context-footer-shortcut"><kbd>←</kbd><kbd>→</kbd> Filter</span>
        <span class="context-footer-shortcut"><kbd>↵</kbd> Select</span>
        <span class="context-footer-shortcut"><kbd>⇧</kbd><kbd>↵</kbd> Multi-add</span>
        <span class="context-footer-shortcut"><kbd>⌫</kbd> Back</span>
    </div>
</ng-template>

<ng-template #viewPopoverTemplate>
    <div class="view-modal" [class.light-mode]="theme.isLight">
        <div class="view-modal-section">
            <span class="view-modal-section-title">Thread layout</span>

            <button type="button"
                    class="view-modal-item"
                    *ngFor="let option of viewOptions"
                    [class.is-active]="option.active"
                    [disabled]="option.disabled"
                    (click)="onViewOptionSelectedFromModal(option.id)">
                <i [class]="option.icon"></i>
                <span class="view-modal-item-copy">
                    <span class="view-modal-item-title">{{ option.label }}</span>
                    <span class="view-modal-item-description">{{ option.description }}</span>
                </span>
                <i class="fa-solid fa-check view-modal-item-check" *ngIf="option.active"></i>
            </button>
        </div>

        <div class="view-modal-section">
            <span class="view-modal-section-title">Message display</span>

            <button type="button"
                    class="view-modal-item"
                    *ngFor="let option of displayModeOptions"
                    [class.is-active]="currentDisplayMode === option.id"
                    (click)="onDisplayModeSelectedFromModal(option.id)">
                <i [class]="option.icon"></i>
                <span class="view-modal-item-copy">
                    <span class="view-modal-item-title">{{ option.label }}</span>
                    <span class="view-modal-item-description">{{ option.description }}</span>
                </span>
                <i class="fa-solid fa-check view-modal-item-check" *ngIf="currentDisplayMode === option.id"></i>
            </button>
        </div>

        <div class="view-modal-section">
            <span class="view-modal-section-title">Inline previews</span>

            <button type="button"
                    class="view-modal-item"
                    *ngFor="let option of inlinePreviewModeOptions"
                    [class.is-active]="renderPreviewsInline === option.enabled"
                    (click)="onInlinePreviewModeSelectedFromModal(option)">
                <i [class]="option.icon"></i>
                <span class="view-modal-item-copy">
                    <span class="view-modal-item-title">{{ option.label }}</span>
                    <span class="view-modal-item-description">{{ option.description }}</span>
                </span>
                <i class="fa-solid fa-check view-modal-item-check" *ngIf="renderPreviewsInline === option.enabled"></i>
            </button>
        </div>
    </div>
</ng-template>

<ng-template #contextUsagePopoverTemplate>
    <div class="context-usage-modal" [class.light-mode]="theme.isLight">
        <div class="context-usage-summary">
            <div class="context-usage-summary-ring"
                 [ngClass]="contextUsageLevelClass"
                 [ngStyle]="contextUsageRingStyle"
                 aria-hidden="true">
                <span>{{ contextUsageButtonLabel }}</span>
            </div>

            <div class="context-usage-summary-copy">
                <span class="context-usage-summary-title">
                    {{ contextFillPercent !== null ? contextFillPercent + '% full' : 'Usage recorded' }}
                </span>
                <span class="context-usage-summary-subtitle">
                    {{ contextUsagePromptLabel }} / {{ contextUsageWindowLabel }} tokens
                </span>
            </div>
        </div>

        <div class="context-usage-grid">
            <div class="context-usage-stat">
                <span>Model</span>
                <strong>{{ contextUsage?.model || currentModelKey || 'Unknown' }}</strong>
            </div>
            <div class="context-usage-stat">
                <span>Prompt tokens</span>
                <strong>{{ contextUsagePromptLabel }}</strong>
            </div>
            <div class="context-usage-stat">
                <span>Context window</span>
                <strong>{{ contextUsageWindowLabel }}</strong>
            </div>
            <div class="context-usage-stat">
                <span>Source</span>
                <strong>{{ contextUsageSourceLabel }}</strong>
            </div>
            <div class="context-usage-stat">
                <span>Updated</span>
                <strong>{{ contextUsageUpdatedAtLabel }}</strong>
            </div>
        </div>

        <div class="context-usage-action">
            <button type="button"
                    class="context-usage-force-btn"
                    (click)="onForceCompactionClick()"
                    [disabled]="!canForceCompaction"
                    [attr.title]="forceCompactionDisabledReason || 'Force compaction without asking the model to call a tool.'">
                <i class="fa-solid fa-compress" *ngIf="!forceCompactionLoading"></i>
                <i class="fa-solid fa-circle-notch fa-spin" *ngIf="forceCompactionLoading"></i>
                <span>{{ forceCompactionLoading ? 'Compacting...' : 'Force compaction' }}</span>
            </button>
            <p class="context-usage-action-note">
                Runs compaction directly on the backend, so it can recover threads that can no longer fit a normal model request.
            </p>
        </div>
    </div>
</ng-template>

<!-- Thread Details Popover -->
<ng-template #threadDetailsPopoverTemplate>
    <app-thread-details-popover
        [embedded]="true"
        [showThreadDetailsPopover]="showThreadDetailsPopover"
        [currentThread]="currentThread"
        [threadMessages]="threadMessages"
        [availableModels]="availableModels"
        [isLocalDevelopment]="isLocalDevelopment"
        [useLocalBackend]="useLocalBackend"
        (renameThreadRequested)="onThreadRenameRequested($event)"
        (backendPreferenceChanged)="onThreadBackendSelected($event)"
        (deleteThreadRequested)="onThreadDeleteRequested()"
        (popoverClosed)="closeSelectorModal()"
        >
    </app-thread-details-popover>
</ng-template>

<ng-template #threadAssetsHeaderTemplate>
    <div class="thread-assets-header" [class.light-mode]="theme.isLight">
        <h3 class="modal-heading">Thread assets</h3>

        <label class="thread-assets-search" [class.light-mode]="theme.isLight">
            <i class="fa-regular fa-magnifying-glass thread-assets-search__icon" aria-hidden="true"></i>
            <input
                #threadAssetSearchInput
                type="text"
                class="thread-assets-search__input"
                placeholder="Search by name or type"
                aria-label="Search thread assets"
                autocomplete="off"
                spellcheck="false"
                [value]="threadAssetSearchQuery"
                (focus)="onThreadAssetSearchFocus()"
                (input)="onThreadAssetSearchInput($event)"
                (keydown)="onThreadAssetSearchKeyDown($event)" />
        </label>
    </div>
</ng-template>

<ng-template #threadAssetsTemplate>
    <div class="thread-assets-modal" [class.light-mode]="theme.isLight" (click)="$event.stopPropagation()">
        <ng-container *ngIf="filteredThreadAssetItems.length > 0; else threadAssetsEmptyState">
            <div class="thread-assets-results">
                <button type="button"
                        #threadAssetItemButton
                        class="thread-assets-item"
                        *ngFor="let asset of filteredThreadAssetItems; let index = index; trackBy: trackThreadAsset"
                        [class.is-focused]="focusedThreadAssetIndex === index"
                        (focus)="onThreadAssetItemFocus(index)"
                        (keydown)="onThreadAssetItemKeyDown($event, index)"
                        (click)="onThreadAssetSelectedFromModal(asset)">
                    <span class="thread-assets-item__icon" aria-hidden="true">
                        <i [class]="asset.iconClass"></i>
                    </span>

                    <span class="thread-assets-item__copy">
                        <span class="thread-assets-item__title-row">
                            <span class="thread-assets-item__title">{{ asset.title }}</span>
                            <span class="thread-assets-item__type">{{ asset.typeLabel }}</span>
                        </span>
                        <span class="thread-assets-item__subtitle" *ngIf="asset.subtitle">{{ asset.subtitle }}</span>
                        <span class="thread-assets-item__detail" *ngIf="asset.detail">{{ asset.detail }}</span>
                    </span>

                    <i class="fa-regular fa-arrow-up-right-from-square thread-assets-item__action" aria-hidden="true"></i>
                </button>
            </div>
        </ng-container>
    </div>
</ng-template>

<ng-template #threadAssetsEmptyState>
    <div class="thread-assets-empty">
        <i [class]="filteredThreadAssetsEmptyIconClass"></i>
        <span>{{ filteredThreadAssetsEmptyLabel }}</span>
    </div>
</ng-template>

<!-- Field Select Options Modal -->
<ng-template #fieldSelectTemplate>
    <div class="field-select-list" *ngIf="fieldSelectField">
        <button type="button" class="field-select-option"
                *ngFor="let opt of fieldSelectField.options"
                [class.is-selected]="fieldSelectField.type === 'multi-select' ? isMultiSelectOptionSelected(fieldSelectField.id, opt.value) : playbookFieldValues[fieldSelectField.id] === opt.value"
                (click)="selectFieldOption(fieldSelectField.id, opt.value)">
            <img *ngIf="opt.icon" [src]="opt.icon" alt="" class="field-select-option-icon" />
            <span>{{ opt.label }}</span>
            <i class="fa-regular fa-check field-select-check"
               *ngIf="fieldSelectField.type === 'multi-select' ? isMultiSelectOptionSelected(fieldSelectField.id, opt.value) : playbookFieldValues[fieldSelectField.id] === opt.value"></i>
        </button>

        <div class="field-select-custom" *ngIf="fieldSelectField.allowCustom">
            <div class="field-select-custom-row">
                <input type="text"
                       class="field-select-custom-input"
                       [placeholder]="fieldSelectField.type === 'multi-select' ? 'Add custom (comma separated)' : 'Type a custom value'"
                       [value]="fieldSelectCustomValue"
                       (input)="fieldSelectCustomValue = $any($event.target).value"
                       (keydown.enter)="applyCustomValue(fieldSelectField.id)" />
                <button type="button" class="field-select-custom-apply" *ngIf="fieldSelectCustomValue.trim()" (click)="applyCustomValue(fieldSelectField.id)">
                    <i class="fa-regular fa-arrow-right"></i>
                </button>
            </div>
        </div>

        <div class="field-select-footer" *ngIf="fieldSelectField.type === 'multi-select'">
            <button type="button" class="field-select-done" (click)="confirmMultiSelect()">Done</button>
        </div>
    </div>
</ng-template>

<!-- Context Field Modal (for playbook context-type fields) -->
<ng-template #contextFieldTemplate>
    <div class="context-field-list" *ngIf="contextFieldActive">
        <!-- Segmented control for business context type -->
        <div class="context-field-segments" *ngIf="contextFieldActive.contextType === 'business'">
            <button type="button"
                    class="context-field-segment"
                    [class.active]="contextFieldSegment === 'existing'"
                    (click)="contextFieldSegment = 'existing'">
                <i class="fa-regular fa-magnifying-glass"></i>
                <span>Select existing</span>
            </button>
            <button type="button"
                    class="context-field-segment"
                    [class.active]="contextFieldSegment === 'new'"
                    (click)="contextFieldSegment = 'new'">
                <i class="fa-regular fa-plus"></i>
                <span>{{ contextFieldActive.newLabel || 'New business' }}</span>
            </button>
        </div>

        <!-- "New" segment content -->
        <div class="context-field-new-panel" *ngIf="contextFieldSegment === 'new' && contextFieldActive.contextType === 'business'">
            <div class="context-field-new-inner">
                <label class="context-field-new-hint">Enter a name or leave blank for a generic placeholder</label>
                <div class="context-field-new-input-row">
                    <input type="text"
                           class="context-field-new-input"
                           [placeholder]="'Business name (optional)'"
                           [value]="contextFieldNewName"
                           (input)="contextFieldNewName = $any($event.target).value"
                           (keydown.enter)="selectContextFieldNew(contextFieldActive)" />
                    <button type="button" class="context-field-new-confirm" (click)="selectContextFieldNew(contextFieldActive)">
                        <i class="fa-regular fa-arrow-right"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- "New" option for non-business context types (keep original inline row) -->
        <div class="context-field-new-row" *ngIf="contextFieldActive.contextType !== 'business'">
            <div class="context-field-new-label">
                <i class="fa-regular fa-plus"></i>
                <span>{{ contextFieldActive.newLabel || 'New' }}</span>
            </div>
            <div class="context-field-new-input-row">
                <input type="text"
                       class="context-field-new-input"
                       [placeholder]="'Name (optional)'"
                       [value]="contextFieldNewName"
                       (input)="contextFieldNewName = $any($event.target).value"
                       (keydown.enter)="selectContextFieldNew(contextFieldActive)" />
                <button type="button" class="context-field-new-confirm" (click)="selectContextFieldNew(contextFieldActive)">
                    <i class="fa-regular fa-arrow-right"></i>
                </button>
            </div>
        </div>

        <!-- Business picker (for contextType: business) — show when "existing" segment active -->
        <ng-container *ngIf="contextFieldActive.contextType === 'business' && contextFieldSegment === 'existing'">
            <app-context-reference-popover
                [embedded]="true"
                [showContextPopover]="true"
                [contextItems]="[]"
                [selectedContextIndex]="0"
                [contextUserObject]="contextUserObject"
                [agencyObject]="agencyObject"
                [availableSources]="['business']"
                [selectionMode]="'select-business'"
                (businessSelected)="selectContextFieldBusiness(contextFieldActive, { id: $event.uid, businessName: $event.name })"
                (businessAttached)="selectContextFieldBusiness(contextFieldActive, { id: $event.uid, businessName: $event.name })"
                (popoverClosed)="closeContextFieldModal()">
            </app-context-reference-popover>
        </ng-container>

        <!-- Item picker (for contextType: proposal, etc.) -->
        <ng-container *ngIf="contextFieldActive.contextType !== 'business'">
            <div class="context-field-depends-hint" *ngIf="contextFieldActive.dependsOn && !playbookContextEntities[contextFieldActive.dependsOn]">
                <i class="fa-regular fa-info-circle"></i>
                <span>Select a {{ contextFieldActive.dependsOn }} first to see existing items</span>
            </div>
            <button type="button" class="context-field-option"
                    *ngFor="let item of contextFieldItems"
                    [class.is-selected]="playbookContextEntities[contextFieldActive.id]?.id === item.id"
                    (click)="selectContextFieldItem(contextFieldActive, item)">
                <i class="fa-regular fa-file-lines"></i>
                <span>{{ item.displayName }}</span>
                <i class="fa-regular fa-check context-field-check"
                   *ngIf="playbookContextEntities[contextFieldActive.id]?.id === item.id"></i>
            </button>
            <div class="context-field-empty" *ngIf="contextFieldActive.dependsOn && playbookContextEntities[contextFieldActive.dependsOn] && contextFieldItems.length === 0">
                <span>No {{ contextFieldActive.contextType }}s found</span>
            </div>
        </ng-container>
    </div>
</ng-template>

<!-- System Prompt Modal -->
<ng-template #systemPromptTemplate>
    <div class="system-prompt-modal">
        <textarea class="system-prompt-textarea"
                  [value]="editableSystemPrompt"
                  (input)="editableSystemPrompt = $any($event.target).value"
                  rows="12"
                  placeholder="System prompt..."></textarea>
        <div class="system-prompt-modal-footer">
            <button type="button" class="system-prompt-reset" *ngIf="hasSystemPromptEdits" (click)="resetSystemPrompt()">Reset</button>
            <button type="button" class="system-prompt-save" (click)="saveSystemPrompt()">Done</button>
        </div>
    </div>
</ng-template>
